import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApimHandler } from '@api/apim-handler';
import { getServicePrincipalToken, getServicePrincipalUsername } from '@api/apim-token';
import { METHODS_WITH_BODY } from '@pages/api-portal/constants';
import { buildRequestUrl, createInitialParams } from '@pages/api-portal/utils';
import { ApiEndpoint, ServicePrincipal, TryItOutExecutionResult } from '@pages/api-portal/types';
import { gatewayUrl } from '@constants/settings';

interface UseTryItOutConsoleOptions {
    endpoint: ApiEndpoint;
}

/*
 * Encapsulates all stateful logic for the Try It Out console: request form state, service
 * principal token acquisition, and executing a real request against the gateway.
 */
export function useTryItOutConsole({ endpoint }: UseTryItOutConsoleOptions) {
    const [pathParams, setPathParams] = useState<Record<string, string>>(() =>
        createInitialParams(endpoint.parameters, 'path'),
    );
    const [queryParams, setQueryParams] = useState<Record<string, string>>(() =>
        createInitialParams(endpoint.parameters, 'query'),
    );
    const [headerParams, setHeaderParams] = useState<Record<string, string>>(() =>
        createInitialParams(endpoint.parameters, 'header'),
    );
    const [requestBody, setRequestBody] = useState<string>(
        endpoint.parameters?.body ? JSON.stringify(endpoint.parameters.body.example, null, 2) : '',
    );

    const [isExecuting, setIsExecuting] = useState(false);
    const [result, setResult] = useState<TryItOutExecutionResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [servicePrincipals, setServicePrincipals] = useState<ServicePrincipal[]>([]);
    const [selectedServicePrincipal, setSelectedServicePrincipal] = useState<string>('');
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [isGettingToken, setIsGettingToken] = useState(false);

    useEffect(() => {
        ApimHandler.fetchServicePrincipals()
            .then(setServicePrincipals)
            .catch(() => setServicePrincipals([]));
    }, []);

    const requestUrl = useMemo(
        () => buildRequestUrl(endpoint.path, endpoint.endpointKey ?? '', pathParams, queryParams),
        [endpoint.path, endpoint.endpointKey, pathParams, queryParams],
    );

    const canExecute = useMemo(
        () => !isExecuting && !!selectedServicePrincipal && !!authToken && !isGettingToken,
        [isExecuting, selectedServicePrincipal, authToken, isGettingToken],
    );

    /*
     * Fetches a bearer token for the selected service principal via a Keycloak password grant.
     */
    useEffect(() => {
        let cancelled = false;

        const getToken = async () => {
            const principal = servicePrincipals.find((sp) => sp.id.toString() === selectedServicePrincipal);
            if (!principal) {
                setAuthToken(null);
                return;
            }

            setIsGettingToken(true);
            try {
                const token = await getServicePrincipalToken(getServicePrincipalUsername(principal.name), principal.password);
                if (!cancelled) setAuthToken(token);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to get authentication token');
                setAuthToken(null);
            } finally {
                if (!cancelled) setIsGettingToken(false);
            }
        };

        if (selectedServicePrincipal) void getToken();
        else setAuthToken(null);

        return () => {
            cancelled = true;
        };
    }, [selectedServicePrincipal, servicePrincipals]);

    /*
     * Executes the request against the gateway using the acquired service-principal token.
     */
    const executeRequest = useCallback(async () => {
        setIsExecuting(true);
        setResult(null);
        setError(null);

        const startTime = performance.now();

        try {
            const url = buildRequestUrl(endpoint.path, endpoint.endpointKey ?? '', pathParams, queryParams);
            const fullUrl = `${gatewayUrl}${url}`;

            const headers: Record<string, string> = { ...headerParams };
            if (!headers['Content-Type'] && !headers['content-type']) {
                headers['Content-Type'] = 'application/json';
            }
            if (authToken && !headers['Authorization'] && !headers['authorization']) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }

            const requestOptions: RequestInit = { method: endpoint.method, headers, mode: 'cors' };

            if (METHODS_WITH_BODY.includes(endpoint.method) && requestBody) {
                try {
                    requestOptions.body = JSON.stringify(JSON.parse(requestBody));
                } catch {
                    throw new Error('Invalid JSON in request body');
                }
            }

            const response = await fetch(fullUrl, requestOptions);
            const latency = Math.round(performance.now() - startTime);
            const contentType = response.headers.get('content-type');
            const responseBody = contentType?.includes('application/json') ? await response.json() : await response.text();

            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value, key) => {
                responseHeaders[key] = value;
            });

            setResult({ status: response.status, statusText: response.statusText, latency, body: responseBody, headers: responseHeaders });
        } catch (err) {
            const latency = Math.round(performance.now() - startTime);
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(errorMessage);
            setResult({ status: 0, statusText: 'Network Error', latency, body: { error: errorMessage } });
        } finally {
            setIsExecuting(false);
        }
    }, [authToken, endpoint.method, endpoint.path, endpoint.endpointKey, headerParams, pathParams, queryParams, requestBody]);

    return {
        pathParams,
        setPathParamValue: (key: string, value: string) => setPathParams((prev) => ({ ...prev, [key]: value })),
        queryParams,
        setQueryParamValue: (key: string, value: string) => setQueryParams((prev) => ({ ...prev, [key]: value })),
        headerParams,
        setHeaderParamValue: (key: string, value: string) => setHeaderParams((prev) => ({ ...prev, [key]: value })),
        requestBody,
        setRequestBody,
        isExecuting,
        result,
        error,
        servicePrincipals,
        selectedServicePrincipal,
        setSelectedServicePrincipal,
        authToken,
        isGettingToken,
        requestUrl,
        canExecute,
        executeRequest,
    };
}
